// Contract per the mflow knowledge base "Autonomous coding agent — full architecture, contracts &
// data model" doc (MVPB-64 epic). Box B (sandbox execution infra, MVPB-73) is provisioned and the
// agent has completed its first successful end-to-end run — no longer blocked on infra.
export type CodeAgentTaskStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type CodeAgentProvider = 'google' | 'openai' | 'anthropic' | 'deepseek';
// MVPB-113 (BE) / MVPB-116 (FE) — server-defined sandbox resource presets, picked by label at task
// start. Never accepted as raw memory/CPU numbers from the client (gateway whitelists the 3 values).
export type CodeAgentTier = 'light' | 'mid' | 'heavy';

export interface CodeAgentLogEntry {
  ts: number;
  message: string;
  isError?: boolean;
}

export interface CodeAgentTask {
  _id: string;
  userId: string;
  prompt: string;
  repoUrl?: string; // "owner/repo" or a full github.com URL — absent means a fresh `git init`
  // Only meaningful alongside repoUrl — explicit branch to check out after cloning (replaces the
  // earlier develop/dev-guessing approach, which didn't generalize across projects). Omitted =
  // whatever the clone's default checkout already is (that repo's own default branch).
  branch?: string;
  aiProvider?: CodeAgentProvider; // absent = mFlow-hosted key (subject to the usage cap); set = BYOK
  tier?: CodeAgentTier; // absent = BE defaults to 'mid'
  // Links this task to a Standalone project task — grants the agent two extra read-only tools
  // (task/project context lookup + scoped knowledge-base search) and, on completion, auto-posts a
  // summary/error comment back onto that task (see the "FE: Coding agent — GitHub status fix, task↔
  // project linking, agent comments" knowledge base doc).
  standaloneTaskId?: string;
  // MF-46/MF-47 — set only on a task the weekly scheduled drift-check job dispatched on its own;
  // `prompt` on these is a verbose investigation prompt the user never wrote, so render it
  // distinctly rather than as a normal user-started task.
  adrCheckDocId?: string;
  // MF-48/MF-49/MF-50 — PR review tracking, polled every 15 minutes once a task's sandbox has
  // opened a PR. `resumedFromTaskId` is set only on a NEW task created by POST .../resume — it
  // points back at the task whose review feedback it's addressing, never the other way around.
  prOwner?: string;
  prRepo?: string;
  prNumber?: number;
  prUrl?: string;
  prHeadBranch?: string;
  prReviewStatus?: 'changes_requested' | 'approved' | 'commented' | 'merged' | 'closed' | null;
  prReviewCheckedAt?: number;
  prUnresolvedCommentCount?: number;
  resumedFromTaskId?: string;
  status: CodeAgentTaskStatus;
  // Only present while status === 'queued' — a place in line by arrival, approximate (a smaller
  // task can skip ahead when the host is short of room; a task also waits while its owner is at
  // their plan's concurrent-task limit). 1 = next.
  queuePosition?: number;
  sandboxId?: string;
  resultSummary?: string;
  error?: string;
  logs: CodeAgentLogEntry[];
  // Real LLM cost, model billing only (never infra). Both absent only if the task failed before
  // any LLM call happened (e.g. sandbox creation failed) — present for every other outcome,
  // including failed-after-some-work and cancelled tasks, since those still burned real tokens.
  tokenUsage?: { inputTokens: number; outputTokens: number };
  costUsd?: number;
  createdAt: number;
  updatedAt: number;
}

export interface CodeAgentTierUsage {
  limit: number | null; // null = unlimited
  used: number;
  remaining: number | null; // null = unlimited
}

export interface CodeAgentUsage {
  plan: string;
  limit: number | null; // null = unlimited
  used: number;
  remaining: number | null; // null = unlimited
  // MF-22 — per-tier sandbox-start quota, independent of the hosted-LLM usage cap above (applies
  // to BYOK tasks too, since it tracks infra starts, not model billing).
  sandboxStarts?: Record<CodeAgentTier, CodeAgentTierUsage>;
}

// Pre-task cost estimate, one entry per provider — 'fallback-assumption' is a documented rough
// placeholder (assumes ~15 agent turns) used until at least one task completes for that provider;
// after that the BE switches it to 'historical-average' automatically, no FE change needed.
export interface CodeAgentCostEstimate {
  provider: CodeAgentProvider;
  model: string;
  estimatedInputTokens: number;
  estimatedOutputTokens: number;
  estimatedCostUsd: number;
  basis: 'historical-average' | 'fallback-assumption';
  sampleSize?: number; // only present when basis is 'historical-average'
}

export const CODE_AGENT_PROVIDER_LABEL: Record<CodeAgentProvider, string> = {
  google: 'Gemini',
  openai: 'OpenAI',
  anthropic: 'Claude',
  deepseek: 'DeepSeek',
};

export const CODE_AGENT_DEFAULT_TIER: CodeAgentTier = 'mid';

export const CODE_AGENT_TIER_ORDER: CodeAgentTier[] = ['light', 'mid', 'heavy'];

export const CODE_AGENT_TIER_LABEL: Record<CodeAgentTier, string> = {
  light: 'Light',
  mid: 'Mid',
  heavy: 'Heavy',
};

// Per MVPB-113's shipped presets (mflow knowledge base "Coding agent sandbox tiers & multi-server
// fleet" doc) — CPU here is the container's CPU-time quota, pinned cores is the separate
// `cpusetCpus` setting that bounds `os.cpus()` inside the sandbox.
export const CODE_AGENT_TIER_RESOURCES: Record<CodeAgentTier, string> = {
  light: '2GB RAM · 1 CPU / 1 pinned core',
  mid: '3GB RAM · 1 CPU / 2 pinned cores',
  heavy: '7GB RAM · 3 CPU / 4 pinned cores',
};

export const CODE_AGENT_TIER_DESCRIPTION: Record<CodeAgentTier, string> = {
  light: 'Landing pages, simple scripts, documentation tweaks.',
  mid: 'Default — a good fit for most typical projects.',
  heavy: 'Large monorepos, intensive frontend builds (CRA, Webpack, Vite, Next.js).',
};

// MF-50 — non-null `prReviewStatus` values only (the field itself may be null/absent for a task
// with no associated PR yet, which callers should already be filtering out before indexing this).
export type PrReviewStatus = NonNullable<CodeAgentTask['prReviewStatus']>;

export const PR_REVIEW_STATUS_LABEL: Record<PrReviewStatus, string> = {
  changes_requested: 'Changes requested',
  approved: 'Approved',
  commented: 'Commented',
  merged: 'Merged',
  closed: 'Closed',
};

export const PR_REVIEW_STATUS_BADGE: Record<PrReviewStatus, string> = {
  changes_requested: 'badge-warning',
  approved: 'badge-success',
  commented: 'badge-info',
  merged: 'badge-success',
  closed: 'badge-ghost',
};

// A resume is only offered while there's real feedback to address, and hidden once the PR is
// settled either way (MF-50: "Hide the action once prReviewStatus reaches merged/closed").
export const PR_REVIEW_RESUMABLE_STATUSES: PrReviewStatus[] = ['changes_requested', 'commented'];

// MF-47 — a task the weekly ADR drift-check job dispatched on its own; `prompt` on these is a
// verbose investigation prompt the user never wrote, so it's never shown raw.
export const isAutomatedAdrCheck = (task: Pick<CodeAgentTask, 'adrCheckDocId'>) => !!task.adrCheckDocId;
export const ADR_CHECK_LABEL = '🔍 Automated: architecture decision check';
